// Dependencies
import { useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import * as zod from "zod";

// Hooks
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";

// Actions
import { loginAction } from "@/actions/auth/login";
import { createAccount } from "@/actions/auth/createAccount";

// Libs
import { CreateAccountSchema } from "@/lib/zod";

// Interfaces
import type {
  CreateAccountController,
  LoginProps,
} from "../interfaces/createAccount.interfaces";

const useCreateAccountController = (): CreateAccountController => {
  const [isPending, startTransition] = useTransition();
  const [captchaResolved, setCaptchaResolved] = useState(false);

  const { toast } = useToast();
  const router = useRouter();

  const form = useForm<zod.infer<typeof CreateAccountSchema>>({
    resolver: zodResolver(CreateAccountSchema),
    defaultValues: {
      nombre: "",
      apellido: "",
      correo: "",
      password: "",
      passwordConfirmation: "",
      rolCartera: {
        id: 2,
      },
      suscripcion: false,
      rolApp: {
        id: 1,
      },
      fechaSuscripcion: new Date().toISOString().split("T")[0],
    },
  });

  const handleCaptcha = (resolved: boolean) => {
    setCaptchaResolved(resolved);
  };

  /**
   * Handles the form submission for account creation
   * @param {zod.infer<typeof CreateAccountSchema>} values - The form values validated by the CreateAccountSchema
   * @returns {Promise<void>} A promise that resolves when the account creation process is complete
   * @throws {Error} If the account creation fails or if there are server errors
   * @description
   * This function:
   * 1. Makes a POST request to create a new account
   * 2. Shows success/error toasts based on the response
   * 3. Attempts to log in the user if account creation is successful
   * 4. Handles various HTTP status codes (201, 400, 403, 500+)
   */
  const handleSubmit = async (
    values: zod.infer<typeof CreateAccountSchema>
  ): Promise<void> => {
    delete values.passwordConfirmation;

    startTransition(async () => {
      try {
        const response = await createAccount(values);

        if (response.status === 201) {
          toast({
            title: "Exito",
            description:
              "Tu cuenta ha sido creada con exito, en un momento seras redireccionado para iniciar sesion.",
            variant: "default",
          });

          await login({
            email: values.correo,
            password: values.password,
          });
        } else if (response.status === 400) {
          toast({
            title: "ERROR",
            description:
              "Hubo un error intentando acceder a tu cuenta. Verifica tus datos.",
            variant: "destructive",
          });

          throw new Error("Contraseña o usuario invalido.");
        } else if (response.status === 403) {
          throw new Error("Contraseña o usuario invalido.");
        } else if (response.status > 499) {
          throw new Error("Contraseña o usuario invalido.");
        }
      } catch (error) {
        toast({
          title: "ERROR",
          description:
            "Hubo un error intentando acceder a tu cuenta. Verifica tus datos.",
          variant: "destructive",
        });
      }
    });
  };

  /**
   * Handles the login process after account creation
   * @param {LoginProps} params - The login credentials
   * @param {string} params.email - The user's email address
   * @param {string} params.password - The user's password
   * @returns {Promise<void>} A promise that resolves when the login process is complete
   * @throws {Error} If the login process fails
   */
  const login = async ({ email, password }: LoginProps): Promise<void> => {
    const res = await loginAction({
      correo: email,
      pwd: password,
    });

    if (res.success) {
      setTimeout(() => {
        router.push("/auth/dashboard");
      }, 2000);
    } else {
      router.push("/accounts/login" + "?success=" + res.success);

      toast({
        title: "ERROR",
        description:
          "Hubo un error intentando acceder a tu cuenta. Verifica tus datos.",
        variant: "destructive",
      });
    }
  };

  return { isPending, form, handleSubmit, handleCaptcha, captchaResolved };
};

export default useCreateAccountController;
